/**
 * Página de Relatórios - Sistema Completo de Relatórios e Métricas
 * 
 * @author Equipe GestaoeCRM
 * @date 02/10/2025
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Calendar, 
  Users, 
  Clock, 
  TrendingUp, 
  BarChart3, 
  Filter,
  Download,
  RefreshCw,
  FileText,
  Activity,
  FolderOpen,
  UserCheck
} from 'lucide-react';
// import WorkSessionReportsWidget from '../components/WorkSessionReportsWidget';
import * as workSessionReportsApi from '../services/workSessionReportsApi';
import * as osActivityLogApi from '../services/osActivityLogApi';
import { reportsApi } from '../services/api';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

interface ReportFilters {
  startDate: string;
  endDate: string;
  userId?: string;
  projectId?: string;
  osId?: string;
}

export default function ReportsPage() {
  const [filters, setFilters] = useState<ReportFilters>({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 dias atrás
    endDate: new Date().toISOString().split('T')[0], // hoje
  });

  const [activeTab, setActiveTab] = useState<'dashboard' | 'sessions' | 'activity' | 'productivity' | 'projects' | 'analysts'>('dashboard');
  
  // Estados para listas de filtros
  const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([]);
  const [users, setUsers] = useState<Array<{ id: string; name: string; email: string }>>([]);

  // Queries para dados dos relatórios
  const { data: dashboardMetrics, isLoading: loadingDashboard } = useQuery({
    queryKey: ['dashboard-metrics'],
    queryFn: () => workSessionReportsApi.getDashboardMetrics(),
  });

  // const { data: periodReport, isLoading: loadingPeriod } = useQuery({
  //   queryKey: ['period-report', filters],
  //   queryFn: () => workSessionReportsApi.generatePeriodReport(filters.startDate, filters.endDate),
  // });

  const { data: activityStats, isLoading: loadingActivity, error: activityStatsError } = useQuery({
    queryKey: ['activity-stats'],
    queryFn: () => osActivityLogApi.getActivityStats(),
  });

  const { data: activityReport, isLoading: loadingActivityReport, error: activityReportError } = useQuery({
    queryKey: ['activity-report-by-area'],
    queryFn: () => osActivityLogApi.getActivityReportByArea(),
  });

  const { data: productivityReport, isLoading: loadingProductivityReport, error: productivityReportError } = useQuery({
    queryKey: ['productivity-report'],
    queryFn: () => osActivityLogApi.getProductivityReport(),
  });

  // Debug: Log da resposta de atividades
  React.useEffect(() => {
    if (activityStats) {
      console.log('[Reports] Resposta completa activityStats:', activityStats);
      console.log('[Reports] activityStats.data:', activityStats.data);
      console.log('[Reports] activityStats.data.actionStats:', activityStats.data?.actionStats);
      console.log('[Reports] activityStats.data.userStats:', activityStats.data?.userStats);
      console.log('[Reports] activityStats.data.dailyActivity:', activityStats.data?.dailyActivity);
    }
    if (activityStatsError) {
      console.error('[Reports] Erro ao buscar estatísticas de atividades:', activityStatsError);
    }
  }, [activityStats, activityStatsError]);

  const handleFilterChange = (key: keyof ReportFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const exportReport = async (type: 'pdf' | 'excel') => {
    try {
      // Implementar exportação de relatórios
      // console.log(`Exportando relatório como ${type}`);
      // TODO: Implementar exportação real
    } catch (error) {
      // console.error('Erro ao exportar relatório:', error);
    }
  };

  // Carregar projetos e usuários para filtros
  React.useEffect(() => {
    const loadFilterData = async () => {
      try {
        const [projectsRes, usersRes] = await Promise.all([
          api.get('/projects', { params: { limit: 100 } }),
          api.get('/users', { params: { limit: 100, active: 'true' } })
        ]);
        setProjects(projectsRes.data.data || []);
        setUsers(usersRes.data.data || []);
      } catch (error) {
        // console.error('Erro ao carregar dados de filtro:', error);
      }
    };
    loadFilterData();
  }, []);

  // Query para relatório de horas por projeto
  const { data: hoursByProject, isLoading: loadingHoursByProject, error: hoursByProjectError } = useQuery({
    queryKey: ['hours-by-project', filters],
    queryFn: () => reportsApi.getHoursByProject({
      projectId: filters.projectId,
      startDate: filters.startDate,
      endDate: filters.endDate,
      userId: filters.userId
    }),
    enabled: activeTab === 'projects'
  });

  // Debug: Log da resposta
  React.useEffect(() => {
    if (hoursByProject) {
      console.log('[Reports] Resposta completa hoursByProject:', hoursByProject);
      console.log('[Reports] hoursByProject.data:', hoursByProject.data);
      console.log('[Reports] hoursByProject.data.data:', hoursByProject.data?.data);
      console.log('[Reports] hoursByProject.data.summary:', hoursByProject.data?.data?.summary);
      console.log('[Reports] hoursByProject.data.report:', hoursByProject.data?.data?.report);
    }
    if (hoursByProjectError) {
      console.error('[Reports] Erro ao buscar horas por projeto:', hoursByProjectError);
    }
  }, [hoursByProject, hoursByProjectError]);

  // Query para relatório de performance de analistas
  const { data: analystPerformance, isLoading: loadingAnalystPerformance, error: analystPerformanceError } = useQuery({
    queryKey: ['analyst-performance', filters],
    queryFn: () => reportsApi.getAnalystPerformance({
      startDate: filters.startDate,
      endDate: filters.endDate,
      userId: filters.userId
    }),
    enabled: activeTab === 'analysts'
  });

  // Debug: Log da resposta de performance
  React.useEffect(() => {
    if (analystPerformance) {
      console.log('[Reports] Resposta completa analystPerformance:', analystPerformance);
      console.log('[Reports] analystPerformance.data:', analystPerformance.data);
      console.log('[Reports] analystPerformance.data.data:', analystPerformance.data?.data);
      console.log('[Reports] analystPerformance.data.summary:', analystPerformance.data?.data?.summary);
      console.log('[Reports] analystPerformance.data.report:', analystPerformance.data?.data?.report);
    }
    if (analystPerformanceError) {
      console.error('[Reports] Erro ao buscar performance de analistas:', analystPerformanceError);
    }
  }, [analystPerformance, analystPerformanceError]);

  // Função para exportar CSV
  const handleExportCSV = async (reportType: 'hours-by-project' | 'analyst-performance') => {
    try {
      let blob;
      let filename = '';

      if (reportType === 'hours-by-project') {
        const response = await reportsApi.exportHoursByProjectCSV({
          projectId: filters.projectId,
          startDate: filters.startDate,
          endDate: filters.endDate,
          userId: filters.userId
        });
        blob = response.data;
        filename = `relatorio_horas_por_projeto_${new Date().toISOString().split('T')[0]}.csv`;
      } else if (reportType === 'analyst-performance') {
        const response = await reportsApi.exportAnalystPerformanceCSV({
          startDate: filters.startDate,
          endDate: filters.endDate,
          userId: filters.userId
        });
        blob = response.data;
        filename = `relatorio_performance_analistas_${new Date().toISOString().split('T')[0]}.csv`;
      }

      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Erro ao exportar CSV:', error);
      alert('Erro ao exportar relatório. Tente novamente.');
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'sessions', label: 'Sessões', icon: Clock },
    { id: 'activity', label: 'Atividades', icon: Activity },
    { id: 'productivity', label: 'Produtividade', icon: TrendingUp },
    { id: 'projects', label: 'Por Projeto', icon: FolderOpen },
    { id: 'analysts', label: 'Performance', icon: UserCheck },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-semibold leading-6 text-text-primary sm:text-2xl sm:truncate">
            Relatórios e Métricas
          </h2>
          <p className="mt-1 text-sm text-text-muted">
            Visualize relatórios detalhados, métricas de produtividade e análises de performance
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
          <button
            onClick={() => exportReport('excel')}
            className="inline-flex items-center px-4 py-2 border border-border-glass rounded-button shadow-glass text-sm font-bold text-text-secondary bg-background-glass backdrop-blur-glass hover:bg-background-glass-hover focus:outline-none focus:ring-2 focus:ring-offset-background-dark focus:ring-blue-500"
          >
            <Download className="h-4 w-4 mr-2" />
            Excel
          </button>
          <button
            onClick={() => exportReport('pdf')}
            className="inline-flex items-center px-4 py-2 border border-border-glass rounded-button shadow-glass text-sm font-bold text-text-secondary bg-background-glass backdrop-blur-glass hover:bg-background-glass-hover focus:outline-none focus:ring-2 focus:ring-offset-background-dark focus:ring-blue-500"
          >
            <FileText className="h-4 w-4 mr-2" />
            PDF
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-background-glass backdrop-blur-glass shadow rounded-glass">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center space-x-4">
            <Filter className="h-5 w-5 text-text-muted" />
            <h3 className="text-lg font-bold text-text-primary">Filtros</h3>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label htmlFor="startDate" className="block text-sm font-bold text-text-secondary">
                Data Inicial
              </label>
              <input
                type="date"
                id="startDate"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-background-glass text-text-primary placeholder-text-muted"
              />
            </div>
            <div>
              <label htmlFor="endDate" className="block text-sm font-bold text-text-secondary">
                Data Final
              </label>
              <input
                type="date"
                id="endDate"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-background-glass text-text-primary placeholder-text-muted"
              />
            </div>
            <div>
              <label htmlFor="userId" className="block text-sm font-bold text-text-secondary">
                Usuário
              </label>
              <select
                id="userId"
                value={filters.userId || ''}
                onChange={(e) => handleFilterChange('userId', e.target.value)}
                className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-background-glass text-text-primary"
              >
                <option value="">Todos os usuários</option>
                {/* TODO: Carregar lista de usuários */}
              </select>
            </div>
            <div>
              <label htmlFor="projectId" className="block text-sm font-bold text-text-secondary">
                Projeto
              </label>
              <select
                id="projectId"
                value={filters.projectId || ''}
                onChange={(e) => handleFilterChange('projectId', e.target.value)}
                className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-background-glass text-text-primary"
              >
                <option value="">Todos os projetos</option>
                {/* TODO: Carregar lista de projetos */}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-background-glass backdrop-blur-glass shadow rounded-glass">
        <div className="border-b border-border-glass">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-secondary'
                      : 'border-transparent text-text-muted hover:text-text-secondary hover:border-border-glass'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-bold text-sm flex items-center space-x-2`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* <WorkSessionReportsWidget variant="full" /> */}
                <div className="bg-background-glass-hover rounded-glass p-6">
                  <h4 className="text-lg font-bold text-text-primary mb-4">Widget de Relatórios</h4>
                  <p className="text-text-muted">Widget temporariamente desabilitado para correção de tipos.</p>
                </div>
                <div className="bg-background-glass-hover rounded-glass p-6">
                  <h4 className="text-lg font-bold text-text-primary mb-4">Métricas Gerais</h4>
                  {loadingDashboard ? (
                    <LoadingSpinner />
                  ) : dashboardMetrics ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-secondary">
                          {(dashboardMetrics as any)?.summary?.totalSessions || 0}
                        </div>
                        <div className="text-sm text-text-muted">Total de Sessões</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-400">
                          {(dashboardMetrics as any)?.summary?.totalHours || '0h'}
                        </div>
                        <div className="text-sm text-text-muted">Horas Trabalhadas</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">
                          {(dashboardMetrics as any)?.summary?.uniqueUsers || 0}
                        </div>
                        <div className="text-sm text-text-muted">Usuários Ativos</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-orange-600">
                          {(dashboardMetrics as any)?.summary?.uniqueOS || 0}
                        </div>
                        <div className="text-sm text-text-muted">OS Concluídas</div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-text-muted">Nenhum dado disponível</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Sessions Tab */}
          {activeTab === 'sessions' && (
            <div className="space-y-4">
              <div className="bg-background-glass-hover rounded-glass p-6">
                <h4 className="text-lg font-bold text-text-primary mb-4">Relatório de Sessões</h4>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-background-glass backdrop-blur-glass p-4 rounded-glass shadow">
                      <div className="text-2xl font-bold text-secondary">
                        {(dashboardMetrics as any)?.summary?.totalSessions || 0}
                      </div>
                      <div className="text-sm text-text-muted">Sessões no Período</div>
                    </div>
                    <div className="bg-background-glass backdrop-blur-glass p-4 rounded-glass shadow">
                      <div className="text-2xl font-bold text-green-400">
                        {(dashboardMetrics as any)?.summary?.totalHours?.toFixed(2) || '0'} h
                      </div>
                      <div className="text-sm text-text-muted">Horas Trabalhadas</div>
                    </div>
                    <div className="bg-background-glass backdrop-blur-glass p-4 rounded-glass shadow">
                      <div className="text-2xl font-bold text-purple-600">
                        {(dashboardMetrics as any)?.trends?.averageSessionDuration?.toFixed(2) || '0'} h
                      </div>
                      <div className="text-sm text-text-muted">Duração Média</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Activity Tab */}
          {activeTab === 'activity' && (
            <div className="space-y-4">
              {/* Estatísticas Resumidas */}
              <div className="bg-background-glass-hover rounded-glass p-6">
                <h4 className="text-lg font-bold text-text-primary mb-4">Estatísticas de Atividades</h4>
                {loadingActivity ? (
                  <LoadingSpinner />
                ) : activityStatsError ? (
                  <div className="text-center py-8">
                    <p className="text-red-400 mb-2">Erro ao carregar estatísticas de atividades</p>
                    <p className="text-sm text-text-muted">
                      {activityStatsError instanceof Error 
                        ? activityStatsError.message 
                        : 'Erro desconhecido'}
                    </p>
                  </div>
                ) : activityStats?.data ? (
                  (() => {
                    const stats = activityStats.data;
                    const hasData = (stats.actionStats && stats.actionStats.length > 0) || 
                                   (stats.userStats && stats.userStats.length > 0) ||
                                   (stats.dailyActivity && stats.dailyActivity.length > 0);
                    
                    if (!hasData) {
                      return (
                        <div className="text-center py-8">
                          <p className="text-text-muted mb-2">Nenhum dado de atividade disponível</p>
                          <p className="text-sm text-text-muted">
                            As estatísticas aparecerão quando houver atividades registradas no sistema.
                          </p>
                        </div>
                      );
                    }

                    const totalActions = stats.actionStats?.reduce((sum: number, item: any) => sum + (item._count?.action || 0), 0) || 0;
                    const today = new Date().toISOString().split('T')[0];
                    const todayActivity = stats.dailyActivity?.find((item: any) => {
                      const itemDate = new Date(item.date).toISOString().split('T')[0];
                      return itemDate === today;
                    });
                    const mostCommonAction = stats.actionStats && stats.actionStats.length > 0 
                      ? stats.actionStats[0]?.action || 'N/A'
                      : 'N/A';

                    return (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-background-glass backdrop-blur-glass p-4 rounded-glass shadow text-center">
                          <div className="text-2xl font-bold text-secondary">
                            {totalActions}
                          </div>
                          <div className="text-sm text-text-muted">Total de Atividades</div>
                        </div>
                        <div className="bg-background-glass backdrop-blur-glass p-4 rounded-glass shadow text-center">
                          <div className="text-2xl font-bold text-green-400">
                            {todayActivity?.count || 0}
                          </div>
                          <div className="text-sm text-text-muted">Atividades Hoje</div>
                        </div>
                        <div className="bg-background-glass backdrop-blur-glass p-4 rounded-glass shadow text-center">
                          <div className="text-2xl font-bold text-purple-600">
                            {stats.userStats?.length || 0}
                          </div>
                          <div className="text-sm text-text-muted">Usuários Ativos</div>
                        </div>
                        <div className="bg-background-glass backdrop-blur-glass p-4 rounded-glass shadow text-center">
                          <div className="text-2xl font-bold text-orange-600">
                            {mostCommonAction}
                          </div>
                          <div className="text-sm text-text-muted">Tipo Mais Comum</div>
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <div className="text-center py-8">
                    <p className="text-text-muted mb-2">Nenhum dado de atividade disponível</p>
                    <p className="text-sm text-text-muted">
                      As estatísticas aparecerão quando houver atividades registradas no sistema.
                    </p>
                  </div>
                )}
              </div>

              {/* Relatório por Área, Categoria e Status */}
              <div className="bg-background-glass-hover rounded-glass p-6">
                <h4 className="text-lg font-bold text-text-primary mb-4">Contagem de Tipo de Ação</h4>
                {loadingActivityReport ? (
                  <LoadingSpinner />
                ) : activityReportError ? (
                  <div className="text-center py-8">
                    <p className="text-red-400 mb-2">Erro ao carregar relatório de atividades</p>
                    <p className="text-sm text-text-muted">
                      {activityReportError instanceof Error 
                        ? activityReportError.message 
                        : 'Erro desconhecido'}
                    </p>
                  </div>
                ) : activityReport?.data ? (
                  (() => {
                    const report = activityReport.data;
                    const statusColumns = [
                      '1.Backlog',
                      '2.Andamento',
                      '3.Teste',
                      '4.Pausa',
                      '6.Pendente Terceiros'
                    ];

                    if (!report.areas || report.areas.length === 0) {
                      return (
                        <div className="text-center py-8">
                          <p className="text-text-muted mb-2">Nenhum dado disponível</p>
                          <p className="text-sm text-text-muted">
                            O relatório aparecerá quando houver OS cadastradas no sistema.
                          </p>
                        </div>
                      );
                    }

                    return (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-border-glass">
                          <thead className="bg-background-glass-hover">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-bold text-text-secondary uppercase">Área Solicitante</th>
                              <th className="px-4 py-3 text-left text-xs font-bold text-text-secondary uppercase">Categ Ordenada</th>
                              {statusColumns.map((status) => (
                                <th key={status} className="px-4 py-3 text-center text-xs font-bold text-text-secondary uppercase">
                                  {status}
                                </th>
                              ))}
                              <th className="px-4 py-3 text-center text-xs font-bold text-text-secondary uppercase">Total Geral</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border-glass bg-background-glass">
                            {report.areas.map((area: any, areaIndex: number) => (
                              <React.Fragment key={area.areaName}>
                                {area.categories.map((category: any, catIndex: number) => (
                                  <tr key={`${area.areaName}-${category.category}`} className="hover:bg-background-glass-hover">
                                    {catIndex === 0 && (
                                      <td rowSpan={area.categories.length} className="px-4 py-3 text-sm font-bold text-text-primary align-top">
                                        {area.areaName}
                                      </td>
                                    )}
                                    <td className="px-4 py-3 text-sm text-text-secondary">{category.category}</td>
                                    {statusColumns.map((status) => (
                                      <td key={status} className="px-4 py-3 text-sm text-center text-text-primary">
                                        {category.statusCounts[status] || ''}
                                      </td>
                                    ))}
                                    <td className="px-4 py-3 text-sm text-center font-bold text-text-primary">
                                      {category.total}
                                    </td>
                                  </tr>
                                ))}
                                <tr className="bg-background-glass-hover font-bold">
                                  <td colSpan={2} className="px-4 py-3 text-sm text-text-primary">
                                    Total {area.areaName}
                                  </td>
                                  {statusColumns.map((status) => {
                                    const areaStatusTotal = area.categories.reduce((sum: number, cat: any) => 
                                      sum + (cat.statusCounts[status] || 0), 0
                                    );
                                    return (
                                      <td key={status} className="px-4 py-3 text-sm text-center text-text-primary">
                                        {areaStatusTotal > 0 ? areaStatusTotal : ''}
                                      </td>
                                    );
                                  })}
                                  <td className="px-4 py-3 text-sm text-center text-text-primary">
                                    {area.total}
                                  </td>
                                </tr>
                              </React.Fragment>
                            ))}
                            {/* Total Geral */}
                            <tr className="bg-background-glass-hover font-bold border-t-2 border-border-glass">
                              <td colSpan={2} className="px-4 py-3 text-sm text-text-primary">
                                Total Geral
                              </td>
                              {statusColumns.map((status) => (
                                <td key={status} className="px-4 py-3 text-sm text-center text-text-primary">
                                  {report.statusTotals[status] || ''}
                                </td>
                              ))}
                              <td className="px-4 py-3 text-sm text-center text-text-primary">
                                {report.grandTotal.total}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    );
                  })()
                ) : (
                  <div className="text-center py-8">
                    <p className="text-text-muted mb-2">Nenhum dado disponível</p>
                    <p className="text-sm text-text-muted">
                      O relatório aparecerá quando houver OS cadastradas no sistema.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Productivity Tab */}
          {activeTab === 'productivity' && (
            <div className="space-y-4">
              {/* Relatório de Produtividade por Categoria e Tipo de Ação */}
              <div className="bg-background-glass-hover rounded-glass p-6">
                <h4 className="text-lg font-bold text-text-primary mb-4">Contagem de Tipo de Ação</h4>
                {loadingProductivityReport ? (
                  <LoadingSpinner />
                ) : productivityReportError ? (
                  <div className="text-center py-8">
                    <p className="text-red-400 mb-2">Erro ao carregar relatório de produtividade</p>
                    <p className="text-sm text-text-muted">
                      {productivityReportError instanceof Error 
                        ? productivityReportError.message 
                        : 'Erro desconhecido'}
                    </p>
                  </div>
                ) : productivityReport?.data ? (
                  (() => {
                    const report = productivityReport.data;
                    const statusColumns = [
                      '1.Backlog',
                      '2.Andamento',
                      '3.Teste',
                      '4.Pausa',
                      '6.Pendente Terceiros'
                    ];

                    if (!report.categories || report.categories.length === 0) {
                      return (
                        <div className="text-center py-8">
                          <p className="text-text-muted mb-2">Nenhum dado disponível</p>
                          <p className="text-sm text-text-muted">
                            O relatório aparecerá quando houver OS cadastradas no sistema.
                          </p>
                        </div>
                      );
                    }

                    return (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-border-glass">
                          <thead className="bg-background-glass-hover">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-bold text-text-secondary uppercase">Categ Ordenada</th>
                              <th className="px-4 py-3 text-left text-xs font-bold text-text-secondary uppercase">Tipo de Ação</th>
                              {statusColumns.map((status) => (
                                <th key={status} className="px-4 py-3 text-center text-xs font-bold text-text-secondary uppercase">
                                  {status}
                                </th>
                              ))}
                              <th className="px-4 py-3 text-center text-xs font-bold text-text-secondary uppercase">Total Geral</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border-glass bg-background-glass">
                            {report.categories.map((category: any, catIndex: number) => (
                              <React.Fragment key={category.category}>
                                {category.actionTypes.map((actionType: any, actionIndex: number) => (
                                  <tr key={`${category.category}-${actionType.actionType}`} className="hover:bg-background-glass-hover">
                                    {actionIndex === 0 && (
                                      <td rowSpan={category.actionTypes.length} className="px-4 py-3 text-sm font-bold text-text-primary align-top">
                                        {category.category}
                                      </td>
                                    )}
                                    <td className="px-4 py-3 text-sm text-text-secondary">{actionType.actionType}</td>
                                    {statusColumns.map((status) => (
                                      <td key={status} className="px-4 py-3 text-sm text-center text-text-primary">
                                        {actionType.statusCounts[status] || ''}
                                      </td>
                                    ))}
                                    <td className="px-4 py-3 text-sm text-center font-bold text-text-primary">
                                      {actionType.total}
                                    </td>
                                  </tr>
                                ))}
                                <tr className="bg-background-glass-hover font-bold">
                                  <td colSpan={2} className="px-4 py-3 text-sm text-text-primary">
                                    Total {category.category}
                                  </td>
                                  {statusColumns.map((status) => {
                                    const categoryStatusTotal = category.actionTypes.reduce((sum: number, at: any) => 
                                      sum + (at.statusCounts[status] || 0), 0
                                    );
                                    return (
                                      <td key={status} className="px-4 py-3 text-sm text-center text-text-primary">
                                        {categoryStatusTotal > 0 ? categoryStatusTotal : ''}
                                      </td>
                                    );
                                  })}
                                  <td className="px-4 py-3 text-sm text-center text-text-primary">
                                    {category.total}
                                  </td>
                                </tr>
                              </React.Fragment>
                            ))}
                            {/* Total Geral */}
                            <tr className="bg-background-glass-hover font-bold border-t-2 border-border-glass">
                              <td colSpan={2} className="px-4 py-3 text-sm text-text-primary">
                                Total Geral
                              </td>
                              {statusColumns.map((status) => (
                                <td key={status} className="px-4 py-3 text-sm text-center text-text-primary">
                                  {report.statusTotals[status] || ''}
                                </td>
                              ))}
                              <td className="px-4 py-3 text-sm text-center text-text-primary">
                                {report.grandTotal.total}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    );
                  })()
                ) : (
                  <div className="text-center py-8">
                    <p className="text-text-muted mb-2">Nenhum dado disponível</p>
                    <p className="text-sm text-text-muted">
                      O relatório aparecerá quando houver OS cadastradas no sistema.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Projects Tab */}
          {activeTab === 'projects' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-bold text-text-primary">Relatório de Horas por Projeto</h4>
                <button
                  onClick={() => handleExportCSV('hours-by-project')}
                  className="inline-flex items-center px-4 py-2 border border-border-glass rounded-button shadow-glass text-sm font-bold text-text-secondary bg-background-glass backdrop-blur-glass hover:bg-background-glass-hover"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Exportar CSV
                </button>
              </div>

              {loadingHoursByProject ? (
                <LoadingSpinner />
              ) : hoursByProject?.data?.data ? (
                <div className="space-y-4">
                  {/* Resumo */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-background-glass backdrop-blur-glass p-4 rounded-glass shadow text-center">
                      <div className="text-2xl font-bold text-secondary">
                        {hoursByProject.data.data.summary?.totalProjects || 0}
                      </div>
                      <div className="text-sm text-text-muted">Projetos</div>
                    </div>
                    <div className="bg-background-glass backdrop-blur-glass p-4 rounded-glass shadow text-center">
                      <div className="text-2xl font-bold text-green-400">
                        {hoursByProject.data.data.summary?.totalHours?.toFixed(2) || '0.00'}h
                      </div>
                      <div className="text-sm text-text-muted">Total Horas</div>
                    </div>
                    <div className="bg-background-glass backdrop-blur-glass p-4 rounded-glass shadow text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        R$ {hoursByProject.data.data.summary?.totalValue?.toFixed(2) || '0.00'}
                      </div>
                      <div className="text-sm text-text-muted">Total Valor</div>
                    </div>
                    <div className="bg-background-glass backdrop-blur-glass p-4 rounded-glass shadow text-center">
                      <div className="text-2xl font-bold text-orange-600">
                        {hoursByProject.data.data.summary?.totalSessions || 0}
                      </div>
                      <div className="text-sm text-text-muted">Sessões</div>
                    </div>
                    <div className="bg-background-glass backdrop-blur-glass p-4 rounded-glass shadow text-center">
                      <div className="text-2xl font-bold text-blue-400">
                        {hoursByProject.data.data.summary?.totalUsers || 0}
                      </div>
                      <div className="text-sm text-text-muted">Usuários</div>
                    </div>
                  </div>

                  {/* Lista de Projetos */}
                  <div className="space-y-4">
                    {hoursByProject.data.data.report?.map((projectData: any) => (
                      <div key={projectData.project.id} className="bg-background-glass backdrop-blur-glass p-6 rounded-glass shadow">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h5 className="text-lg font-bold text-text-primary">{projectData.project.name}</h5>
                            <p className="text-sm text-text-muted">{projectData.project.client?.name || 'Sem cliente'}</p>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-secondary">{projectData.totalHours.toFixed(2)}h</div>
                            <div className="text-sm text-text-muted">R$ {projectData.totalValue.toFixed(2)}</div>
                          </div>
                        </div>

                        {/* Detalhes por Usuário */}
                        {projectData.users && projectData.users.length > 0 && (
                          <div className="mt-4 border-t border-border-glass pt-4">
                            <h6 className="text-sm font-bold text-text-secondary mb-2">Por Analista:</h6>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                              {projectData.users.map((user: any) => (
                                <div key={user.user.id} className="bg-background-glass-hover p-3 rounded-glass">
                                  <div className="font-bold text-text-primary text-sm">{user.user.name}</div>
                                  <div className="text-xs text-text-muted">{user.totalHours.toFixed(2)}h - R$ {user.totalValue.toFixed(2)}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-text-muted">Nenhum dado disponível para o período selecionado.</p>
              )}
            </div>
          )}

          {/* Analysts Performance Tab */}
          {activeTab === 'analysts' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-bold text-text-primary">Performance de Analistas</h4>
                <button
                  onClick={() => handleExportCSV('analyst-performance')}
                  className="inline-flex items-center px-4 py-2 border border-border-glass rounded-button shadow-glass text-sm font-bold text-text-secondary bg-background-glass backdrop-blur-glass hover:bg-background-glass-hover"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Exportar CSV
                </button>
              </div>

              {loadingAnalystPerformance ? (
                <LoadingSpinner />
              ) : analystPerformance?.data?.data ? (
                <div className="space-y-4">
                  {/* Resumo */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-background-glass backdrop-blur-glass p-4 rounded-glass shadow text-center">
                      <div className="text-2xl font-bold text-secondary">
                        {analystPerformance.data.data.summary?.totalAnalysts || 0}
                      </div>
                      <div className="text-sm text-text-muted">Analistas</div>
                    </div>
                    <div className="bg-background-glass backdrop-blur-glass p-4 rounded-glass shadow text-center">
                      <div className="text-2xl font-bold text-green-400">
                        {analystPerformance.data.data.summary?.totalHours?.toFixed(2) || '0.00'}h
                      </div>
                      <div className="text-sm text-text-muted">Total Horas</div>
                    </div>
                    <div className="bg-background-glass backdrop-blur-glass p-4 rounded-glass shadow text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        {analystPerformance.data.data.summary?.averageEfficiency || 0}%
                      </div>
                      <div className="text-sm text-text-muted">Eficiência Média</div>
                    </div>
                    <div className="bg-background-glass backdrop-blur-glass p-4 rounded-glass shadow text-center">
                      <div className="text-2xl font-bold text-orange-600">
                        {analystPerformance.data.data.summary?.averageOnTimeCompletion || 0}%
                      </div>
                      <div className="text-sm text-text-muted">Conclusão no Prazo</div>
                    </div>
                  </div>

                  {/* Tabela de Performance */}
                  <div className="bg-background-glass backdrop-blur-glass rounded-glass shadow overflow-hidden">
                    <table className="min-w-full divide-y divide-border-glass">
                      <thead className="bg-background-glass-hover">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-bold text-text-secondary uppercase">Analista</th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-text-secondary uppercase">Horas</th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-text-secondary uppercase">Valor</th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-text-secondary uppercase">Sessões</th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-text-secondary uppercase">OS</th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-text-secondary uppercase">Eficiência</th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-text-secondary uppercase">No Prazo</th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-text-secondary uppercase">Produtividade</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border-glass">
                        {analystPerformance.data.data.report?.map((analyst: any) => (
                          <tr key={analyst.user.id} className="hover:bg-background-glass-hover">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="font-bold text-text-primary">{analyst.user.name}</div>
                              <div className="text-sm text-text-muted">{analyst.user.email}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-text-primary">{analyst.totalHours.toFixed(2)}h</td>
                            <td className="px-6 py-4 whitespace-nowrap text-text-primary">R$ {analyst.totalValue.toFixed(2)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-text-primary">{analyst.totalSessions}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-text-primary">{analyst.totalOSCount}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                analyst.efficiency >= 90 ? 'bg-green-500/20 text-green-400' :
                                analyst.efficiency >= 70 ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-red-500/20 text-red-400'
                              }`}>
                                {analyst.efficiency}%
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                analyst.onTimeCompletionRate >= 80 ? 'bg-green-500/20 text-green-400' :
                                analyst.onTimeCompletionRate >= 60 ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-red-500/20 text-red-400'
                              }`}>
                                {analyst.onTimeCompletionRate}%
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-text-primary">R$ {analyst.productivity.toFixed(2)}/h</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p className="text-text-muted">Nenhum dado disponível para o período selecionado.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
